/**
 * BreadcrumbTailContext (non-component module)
 *
 * The Context instance + hooks live here so React Fast Refresh can
 * preserve the provider across edits. The Provider component lives in
 * `BreadcrumbTailProvider.tsx` — when you edit one of them, Fast Refresh
 * only remounts the edited file and the Context identity stays stable
 * (avoiding the "useX must be used within a Provider" ghost error we saw
 * with AuthContext when hooks + components + context shared one file).
 *
 * Consumers:
 *   - `<Breadcrumbs />` reads the tail via `useBreadcrumbTailValue()`.
 *   - Pages push tail crumbs via `useBreadcrumbTail([{ label: ... }])`.
 *   - `<BreadcrumbTailProvider>` in `DashboardLayout` owns the state.
 */

import { createContext, useContext, useEffect } from 'react';

export interface BreadcrumbCrumb {
  label: string;
  /** Optional href — last crumb is rendered as plain text regardless. */
  href?: string;
}

export interface BreadcrumbTailContextValue {
  tail: BreadcrumbCrumb[];
  setTail: (crumbs: BreadcrumbCrumb[] | null) => void;
}

export const BreadcrumbTailContext = createContext<
  BreadcrumbTailContextValue | undefined
>(undefined);

/**
 * Read-only access to the current tail. Use from the component that
 * renders the breadcrumb (`Breadcrumbs.tsx`).
 */
export function useBreadcrumbTailValue(): BreadcrumbCrumb[] {
  const ctx = useContext(BreadcrumbTailContext);
  // Tolerate absence: breadcrumb components rendered outside the layout
  // (e.g. Storybook) should still render the URL-derived fallback.
  return ctx?.tail ?? [];
}

/**
 * Push tail crumbs for the lifetime of the calling component. Pass `null`
 * or an empty array to clear. The tail is cleared automatically on
 * unmount so users can navigate away without leftover crumbs.
 *
 * Re-invoking with different content updates the breadcrumb without
 * remounting it.
 */
export function useBreadcrumbTail(
  crumbs: BreadcrumbCrumb[] | null | undefined,
): void {
  const ctx = useContext(BreadcrumbTailContext);
  // Stringify for a stable effect dependency — new array identities from
  // the caller shouldn't re-fire the effect if the content is the same.
  const key = crumbs ? JSON.stringify(crumbs) : '';

  useEffect(() => {
    if (!ctx) return;
    ctx.setTail(crumbs ?? null);
    return () => {
      ctx.setTail(null);
    };
    // We only want to react to the stringified snapshot, not to `ctx`
    // or `crumbs` identity churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
